import { debounce } from 'lodash-es'
import type { DirectiveBinding } from 'vue'

type ObserveHandler = (
  isIntersecting: boolean,
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver
) => void

export interface ObserveDirectiveBinding
  extends Omit<DirectiveBinding, 'modifiers' | 'value'> {
  value?:
    | ObserveHandler
    | { handler: ObserveHandler; options?: IntersectionObserverInit }
  modifiers: {
    once?: boolean
  }
}

const ThrottleLimit = 100

function mounted(el: HTMLElement, binding: ObserveDirectiveBinding) {
  if (!IntersectionObserver) {
    return
  }
  const modifiers = binding.modifiers || {}
  const value = binding.value
  const { handler, options } =
    typeof value === 'object' ? value : { handler: value, options: {} }

  const debouncedHandler = handler
    ? debounce(handler, ThrottleLimit)
    : undefined

  const observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[] = []) => {
      const _observe = el._observe?.[binding.instance!.$.uid]
      if (!_observe) {
        return
      }

      const isIntersecting = entries.some((entry) => entry.isIntersecting)
      if (
        debouncedHandler &&
        (!modifiers.once || isIntersecting || _observe.init)
      ) {
        debouncedHandler(isIntersecting, entries, observer)
      }

      if (isIntersecting && modifiers.once) {
        unmounted(el, binding)
      } else {
        _observe.init = true
      }
    },
    options
  )

  el._observe = Object(el._observe)
  el._observe![binding.instance!.$.uid] = { init: false, observer }

  observer.observe(el)
}

function unmounted(el: HTMLElement, binding: ObserveDirectiveBinding) {
  if (!IntersectionObserver) {
    return
  }
  const observe = el._observe?.[binding.instance!.$.uid]
  if (!observe) {
    return
  }

  observe.observer.unobserve(el)
  delete el._observe![binding.instance!.$.uid]
}

export const Intersect = {
  mounted,
  unmounted,
}

export default Intersect
